import React, { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import moment from "moment"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import ChartInfo from "./ChartInfo"

const INITIAL_FILTERS = {
  technician_id: "",
  technician_type: "",
  date_from: null,
  date_to: null,
  postcode: "",
}

function TechniciansWorkload() {
  const [loading, setLoading] = useState(true)
  const [labels, setLabels] = useState([])
  const [historical, setHistorical] = useState([])
  const [predicted, setPredicted] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [technicians, setTechnicians] = useState([])

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)

    const f = `${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`

    Api.get(
      `analytics/technicians-workload?technician_id=${filters.technician_id}${filters.technician_type ? `&technician_type=${filters.technician_type}` : ""}&postcode=${filters.postcode}${f}`,
    )
      .then((res) => {
        setLabels(
          res.data.chart_data.labels?.map((date) => {
            return moment(date, "DD.MM").isSame(moment(), "day")
              ? trans("analytics.today")
              : date
          }) || [],
        )
        setHistorical(res.data.chart_data.historical || [])
        setPredicted(res.data.chart_data.predicted || [])
        setTechnicians(res.data.technicians || [])

        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  const historicalSeries = labels.map((_, i) =>
    i < historical.length ? (historical[i] ?? null) : null,
  )

  const boundaryIndex = historical.length - 1
  const predictedSeries = labels.map((_, i) => {
    if (i === boundaryIndex) return historical[boundaryIndex] ?? null
    if (i > boundaryIndex) return predicted[i - historical.length] ?? null
    return null
  })

  function hasAnyData() {
    return labels.length > 0
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.technician_id) count++
    if (filters.postcode) count++
    if (filters.date_from || filters.date_to) count++
    return count
  }

  return (
    <div className="repair-requests-chart">
      <div className="head">
        <h3>
          {trans("analytics.technicianLoad")}
          <ChartInfo name="techniciansWorkload" />
        </h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <select
                  value={filters.technician_id || ""}
                  name="technician_id"
                  onChange={(e) => {
                    const option = e.target.options[e.target.selectedIndex]

                    setFilters((prev) => ({
                      ...prev,
                      technician_id: e.target.value,
                      technician_type:
                        option?.getAttribute("data-type") || null,
                    }))
                  }}
                >
                  <option value="">{trans("random.allUsers")}</option>
                  {technicians.map((technician) => (
                    <option
                      key={technician.id}
                      value={technician.id}
                      data-type={technician.type_id}
                    >
                      {technician.full_name || technician.name || "-"}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <input
                  type="text"
                  value={filters.postcode || ""}
                  name="postcode"
                  onChange={(e) =>
                    handleFilterChange("postcode", e.target.value)
                  }
                  placeholder={trans("labels.postCode")}
                />
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : !hasAnyData() ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <LineChart
          xAxis={[{ scaleType: "point", data: labels }]}
          series={[
            {
              id: "tw-hist",
              curve: "linear",
              label: trans("analytics.historicalData"),
              area: true,
              data: historicalSeries,
              color: "#181F67",
              valueFormatter: (v) => (v == null ? "" : String(v)),
            },
            {
              id: "tw-pred",
              curve: "linear",
              label: trans("analytics.forecast"),
              data: predictedSeries,
              color: "#181F67",
              valueFormatter: (v) => (v == null ? "" : String(v)),
            },
          ]}
          height={400}
          sx={{
            "& .MuiLineElement-series-tw-pred": { strokeDasharray: "6 3" },
            "& .MuiAreaElement-series-tw-hist": {
              fill: "url(#tw-hist-gradient)",
            },
            "& .MuiLineElement-root": { strokeWidth: 1 },
          }}
          slotProps={{
            legend: { hidden: true },
          }}
        >
          <defs>
            <linearGradient id="tw-hist-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#181F67" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#181F67" stopOpacity={0.02} />
            </linearGradient>
          </defs>
        </LineChart>
      )}
    </div>
  )
}

export default TechniciansWorkload
